<template>
  <div>
    <div class="flex justify-between items-center mb-4">
      <div class="text-neutral-900 text-base font-semibold">
        Project Files
        <span class="text-slate-400 font-normal">({{ files.length }})</span>
      </div>
      <button
        @click="uploadOpen = true"
        class="px-4 py-2 bg-blue_primary text-white text-sm rounded-lg"
      >
        Upload files
      </button>
    </div>

    <LoaderSpinner v-if="flagLoading" />

    <div
      v-else-if="files.length === 0"
      class="w-full p-10 bg-white rounded-[20px] border border-slate-200 flex flex-col items-center gap-2"
    >
      <div class="text-slate-600 text-sm font-medium">No files yet</div>
      <div class="text-slate-400 text-sm">
        Upload documents to keep them with this project.
      </div>
    </div>

    <!-- Desktop -->
    <div
      v-else-if="!isMobileView"
      class="w-full bg-white rounded-bl-[20px] rounded-br-[20px] flex flex-col"
    >
      <div
        class="h-11 bg-slate-50 border-b border-slate-200 flex items-center text-neutral-900 text-sm font-medium font-['Public Sans']"
      >
        <div class="w-[40%] px-4">File</div>
        <div class="w-[22%] px-2">Uploaded by</div>
        <div class="w-[15%] px-4">Date</div>
        <div class="w-[11%] px-4">Size</div>
        <div class="w-[12%] px-4">Actions</div>
      </div>
      <div
        v-for="file in files"
        :key="file.id"
        class="h-[72px] border-b border-slate-200 flex items-center text-slate-600 text-sm font-normal font-['Public Sans']"
      >
        <div class="w-[40%] px-4 flex items-center gap-3 min-w-0">
          <div
            class="w-10 h-10 shrink-0 rounded-[20px] bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-500 uppercase"
          >
            {{ getExtension(file.original_name) }}
          </div>
          <span class="truncate text-left" :title="file.original_name">
            {{ file.original_name }}
          </span>
        </div>
        <div class="w-[22%] px-2 truncate">{{ uploaderName(file) }}</div>
        <div class="w-[15%] px-4">
          {{ helpers.formatDateToDDMMMYYYY(file.created_at) }}
        </div>
        <div class="w-[11%] px-4">{{ formatSize(file.size_bytes) }}</div>
        <div class="w-[12%] px-4 flex gap-3">
          <button
            @click="download(file)"
            :disabled="downloadingId === file.id"
            class="text-blue_primary hover:underline"
          >
            {{ downloadingId === file.id ? "..." : "Download" }}
          </button>
          <button
            v-if="canDelete(file)"
            @click="fileToDelete = file"
            class="text-red-600 hover:underline"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        v-for="file in files"
        :key="file.id"
        class="bg-white rounded-lg shadow p-4 border border-slate-200 flex flex-col gap-2"
      >
        <div class="text-slate-600 text-sm font-medium break-all">
          {{ file.original_name }}
        </div>
        <div class="text-slate-400 text-xs">
          {{ uploaderName(file) }} ·
          {{ helpers.formatDateToDDMMMYYYY(file.created_at) }} ·
          {{ formatSize(file.size_bytes) }}
        </div>
        <div class="flex gap-4 text-sm">
          <button @click="download(file)" class="text-blue_primary">
            Download
          </button>
          <button
            v-if="canDelete(file)"
            @click="fileToDelete = file"
            class="text-red-600"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <ProjectFilesUpload
      :isOpen="uploadOpen"
      :projectId="projectId"
      @close="uploadOpen = false"
      @uploaded="fetchFiles"
    />

    <!-- Confirmación de borrado -->
    <div
      v-if="fileToDelete"
      class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
    >
      <div class="w-[572px] max-w-[95vw] bg-white rounded-xl shadow flex flex-col">
        <div
          class="p-3 bg-slate-100 rounded-tl-xl rounded-tr-xl border-b border-slate-200 text-neutral-900 text-lg font-semibold"
        >
          Confirm Delete
        </div>
        <div class="p-4 flex flex-col gap-4">
          <div class="text-slate-500 text-base font-normal leading-relaxed">
            Are you sure you want to delete this file?
          </div>
          <div class="w-full px-5 py-2.5 bg-red-600 rounded text-white text-sm font-medium break-all">
            {{ fileToDelete.original_name }}
          </div>
        </div>
        <div class="flex justify-end gap-4 p-4">
          <button
            @click="fileToDelete = null"
            :disabled="deleting"
            class="px-4 py-2 bg-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            @click="confirmDelete"
            :disabled="deleting"
            class="px-4 py-2 bg-red-600 text-white rounded-lg"
          >
            {{ deleting ? "Deleting..." : "Confirm" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import LoaderSpinner from "../../CircularLoader.vue";
import ProjectFilesUpload from "./ProjectFilesUpload.vue";
import helpers from "../../../helpers";
import { formatSize, getExtension } from "./file-rules";

export default {
  components: {
    LoaderSpinner,
    ProjectFilesUpload,
  },

  setup() {
    const store = useStore();
    const route = useRoute();
    const projectId = route.params.id;
    const files = ref([]);
    const flagLoading = ref(true);
    const uploadOpen = ref(false);
    const fileToDelete = ref(null);
    const deleting = ref(false);
    const downloadingId = ref(null);
    const isMobileView = computed(() => store.getters.isMobileView);
    const currentUser = computed(() => store.state.user);

    async function fetchFiles() {
      flagLoading.value = true;
      try {
        const response = await store.dispatch("getProjectFiles", projectId);
        files.value = response.ok ? response.files : [];
      } catch (error) {
        files.value = [];
      } finally {
        flagLoading.value = false;
      }
    }

    function uploaderName(file) {
      return file.uploader
        ? `${file.uploader.name} ${file.uploader.last_name}`
        : "-";
    }

    // Misma regla que el servidor: admin borra todo, el resto solo lo suyo
    function canDelete(file) {
      if (!currentUser.value) return false;
      return (
        currentUser.value.type === 1 ||
        Number(currentUser.value.uid) === file.uploaded_by
      );
    }

    async function download(file) {
      downloadingId.value = file.id;
      try {
        const response = await store.dispatch("getProjectFileDownloadUrl", {
          id_project: projectId,
          id_file: file.id,
        });
        if (response.ok) {
          // La URL firmada fuerza la descarga (Content-Disposition: attachment)
          const link = document.createElement("a");
          link.href = response.url;
          link.rel = "noopener";
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      } finally {
        downloadingId.value = null;
      }
    }

    async function confirmDelete() {
      deleting.value = true;
      try {
        const response = await store.dispatch("deleteProjectFile", {
          id_project: projectId,
          id_file: fileToDelete.value.id,
        });
        if (response.ok) {
          files.value = files.value.filter((f) => f.id !== fileToDelete.value.id);
          fileToDelete.value = null;
        }
      } finally {
        deleting.value = false;
      }
    }

    onMounted(fetchFiles);

    return {
      projectId,
      files,
      flagLoading,
      uploadOpen,
      fileToDelete,
      deleting,
      downloadingId,
      isMobileView,
      helpers,
      fetchFiles,
      uploaderName,
      canDelete,
      download,
      confirmDelete,
      formatSize,
      getExtension,
    };
  },
};
</script>

<style scoped>
</style>
